import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Layout from '../../components/Layout';
import Loading from '../../components/Loading';
import { useToast } from '../../components/Toast';
import {
  fetchCouponTypes,
  createCouponType,
  updateCouponType,
  deleteCouponType,
  toggleSellable,
  setCurrentPage,
  openCreateModal,
  closeCreateModal,
  openEditModal,
  closeEditModal,
  upsertCouponTypeFromSocket,
  removeCouponTypeFromSocket,
} from '../../store/slices/couponTypeSlice';
import { useCouponTypeSocket } from '../../hooks/useSocketListeners';
import './CouponType.css';

const ITEMS_PER_PAGE = 9;

const formatValidity = (validityType, validityValue) => {
  if (validityType === 'days') {
    return `${validityValue}日間`;
  } else if (validityType === 'times') {
    return `${validityValue}回`;
  }
  return '-';
};

const formatAmount = (amount) => {
  if (amount === 0 || amount === '0') return '無料';
  return `${amount}円`;
};

const CouponType = () => {
  const dispatch = useDispatch();
  const toast = useToast();
  const {
    couponTypes,
    currentPage,
    isLoading,
    isCreateModalOpen,
    isEditModalOpen,
    editingType,
  } = useSelector((state) => state.couponType);

  const [formData, setFormData] = useState({
    ctCouponType: 'charge',
    ctName: '',
    ctAmountValue: '',
    ctNumUse: 1,
    ctExpiryDate: 0,
    ctFlagShip: true,
  });

  const totalPages = Math.ceil(couponTypes.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentData = couponTypes.slice(startIndex, endIndex);

  useEffect(() => {
    dispatch(fetchCouponTypes());
  }, [dispatch]);

  const handleCouponTypeSocketEvent = useCallback(
    (e) => {
      const action = e?.action;
      const payload = e?.couponType ?? e?.data;

      if (action === 'deleted') {
        dispatch(removeCouponTypeFromSocket(payload));
        return;
      }
      if (action === 'created' || action === 'updated') {
        dispatch(upsertCouponTypeFromSocket(payload));
        return;
      }

      // Fallback when payload shape is unexpected
      dispatch(fetchCouponTypes());
    },
    [dispatch]
  );

  useCouponTypeSocket(handleCouponTypeSocketEvent);

  useEffect(() => {
    if (isCreateModalOpen) {
      setFormData({
        ctCouponType: 'charge',
        ctName: '',
        ctAmountValue: '',
        ctNumUse: 1,
        ctExpiryDate: 0,
        ctFlagShip: true,
      });
    }
  }, [isCreateModalOpen]);

  useEffect(() => {
    if (isEditModalOpen && editingType) {
      setFormData({
        ctCouponType: editingType.ctCouponType || editingType.couponType || 'charge',
        ctName: editingType.ctName || editingType.name || '',
        ctAmountValue: editingType.ctAmountValue || editingType.amountValue || '',
        ctNumUse: editingType.ctNumUse || editingType.numUse || 1,
        ctExpiryDate:
          editingType.ctExpiryDate !== undefined && editingType.ctExpiryDate !== null
            ? editingType.ctExpiryDate
            : 0,
        ctFlagShip: editingType.ctFlagShip !== undefined ? editingType.ctFlagShip : true,
      });
    }
  }, [isEditModalOpen, editingType]);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      dispatch(setCurrentPage(page));
    }
  };

  const handleToggleSellable = (id, currentValue) => {
    dispatch(toggleSellable({ id, sellable: !currentValue }));
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    const result = await dispatch(createCouponType(formData));

    if (createCouponType.fulfilled.match(result)) {
      toast.success('クーポン種類を作成しました');
    } else {
      const msg =
        result?.payload ||
        result?.error?.message ||
        'クーポン種類の作成に失敗しました';
      toast.error(msg);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    const result = await dispatch(
      updateCouponType({ id: editingType.id, data: { ctFlagShip: formData.ctFlagShip } })
    );

    if (updateCouponType.fulfilled.match(result)) {
      toast.success('クーポン種類を更新しました');
    } else {
      const msg =
        result?.payload ||
        result?.error?.message ||
        'クーポン種類の更新に失敗しました';
      toast.error(msg);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('本当に削除しますか？')) return;

    const result = await dispatch(deleteCouponType(id));

    if (deleteCouponType.fulfilled.match(result)) {
      toast.success('クーポン種類を削除しました');
    } else {
      const msg =
        result?.payload ||
        result?.error?.message ||
        'クーポン種類の削除に失敗しました';
      toast.error(msg);
    }
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        pages.push(1, 2, 3, '...', totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1, '...', totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, '...', currentPage, '...', totalPages);
      }
    }

    return pages;
  };

  const renderForm = (isEdit = false) => (
    <>
      <div className="coupon-type__form-group">
        <label className="coupon-type__form-label">クーポンタイプ</label>
        <div className="coupon-type__form-radio-group">
          <label className="coupon-type__form-radio">
            <input
              type="radio"
              name={`ctCouponType-${isEdit ? 'edit' : 'create'}`}
              value="charge"
              checked={formData.ctCouponType === 'charge'}
              onChange={(e) => setFormData({ ...formData, ctCouponType: e.target.value })}
              className="coupon-type__form-radio-input"
              disabled={isEdit}
            />
            <span className="coupon-type__form-radio-custom"></span>
            <span className="coupon-type__form-radio-label">チャージ (Charge)</span>
          </label>
          <label className="coupon-type__form-radio">
            <input
              type="radio"
              name={`ctCouponType-${isEdit ? 'edit' : 'create'}`}
              value="freeTrial"
              checked={formData.ctCouponType === 'freeTrial'}
              onChange={(e) => setFormData({ ...formData, ctCouponType: e.target.value })}
              className="coupon-type__form-radio-input"
              disabled={isEdit}
            />
            <span className="coupon-type__form-radio-custom"></span>
            <span className="coupon-type__form-radio-label">無料体験 (Free Trial)</span>
          </label>
        </div>
      </div>

      <div className="coupon-type__form-group">
        <label className="coupon-type__form-label">クーポン名</label>
        <input
          type="text"
          className="coupon-type__form-input"
          value={formData.ctName}
          onChange={(e) => setFormData({ ...formData, ctName: e.target.value })}
          placeholder="例: 100円クーポン"
          required
          disabled={isEdit}
        />
      </div>

      <div className="coupon-type__form-group">
        <label className="coupon-type__form-label">
          {formData.ctCouponType === 'charge' ? '金額 (円)' : '日数'}
        </label>
        <div className="coupon-type__form-input-group">
          <input
            type="number"
            step="1"
            className="coupon-type__form-input coupon-type__form-input--number"
            value={formData.ctAmountValue}
            onChange={(e) => setFormData({ ...formData, ctAmountValue: e.target.value })}
            placeholder="0"
            min="0"
            required
            disabled={isEdit}
          />
          <span className="coupon-type__form-input-suffix">
            {formData.ctCouponType === 'charge' ? '円' : '日'}
          </span>
        </div>
        <span className="coupon-type__form-hint">
          {formData.ctCouponType === 'charge'
            ? '※ チャージタイプの場合、金額を入力してください'
            : '※ 無料体験タイプの場合、日数を入力してください'
          }
        </span>
      </div>

      {/* <div className="coupon-type__form-group" >
        <label className="coupon-type__form-label">使用可能回数</label>
        <div className="coupon-type__form-input-group">
          <input
            type="number"
            step="1"
            className="coupon-type__form-input coupon-type__form-input--number"
            value={formData.ctNumUse}
            onChange={(e) => setFormData({ ...formData, ctNumUse: parseFloat(e.target.value) || null })}
            min="1"
            required
            disabled={isEdit}
          />
          <span className="coupon-type__form-input-suffix">回</span>
        </div>
        <span className="coupon-type__form-hint">※ クーポンが使用できる回数（デフォルト: 1回）</span>
      </div> */}

      <div className="coupon-type__form-group">
        <label className="coupon-type__form-label">有効期限（日数）</label>
        <div className="coupon-type__form-input-group">
          <input
            type="number"
            step="1"
            className="coupon-type__form-input coupon-type__form-input--number"
            value={formData.ctExpiryDate}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === '') {
                setFormData({ ...formData, ctExpiryDate: 0 });
                return;
              }
              const parsed = parseInt(raw, 10);
              setFormData({
                ...formData,
                ctExpiryDate: Number.isNaN(parsed) ? 0 : parsed,
              });
            }}
            placeholder="0"
            min="0"
            disabled={isEdit}
          />
          <span className="coupon-type__form-input-suffix">日</span>
        </div>
        <span className="coupon-type__form-hint">※ 空欄または0の場合は有効期限なし（デフォルト: なし）</span>
      </div>

      <div className="coupon-type__form-group coupon-type__form-group--checkbox">
        <label className="coupon-type__form-checkbox-label">
          <input
            type="checkbox"
            checked={formData.ctFlagShip}
            onChange={(e) => setFormData({ ...formData, ctFlagShip: e.target.checked })}
            className="coupon-type__form-checkbox"
          />
          <span className="coupon-type__form-checkbox-custom">
            <svg viewBox="0 0 24 24" className="coupon-type__form-checkbox-icon">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" fill="currentColor"/>
            </svg>
          </span>
          <span>フラグシップ</span>
        </label>
        <span className="coupon-type__form-hint">※ デフォルト: 有効</span>
      </div>
    </>
  );

  return (
    <Layout>
      <div className="coupon-type">
        <div className="coupon-type__card">
          <h1 className="coupon-type__title">種類生成</h1>

          <div className="coupon-type__scroll-area">
            <button
              className="coupon-type__create-btn"
              onClick={() => dispatch(openCreateModal())}
            >
              新規作成
              <span className="coupon-type__create-btn-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="coupon-type__create-btn-icon-svg">
                  <path fillRule="evenodd" d="M12 3.75a.75.75 0 0 1 .75.75v6.75h6.75a.75.75 0 0 1 0 1.5h-6.75v6.75a.75.75 0 0 1-1.5 0v-6.75H4.5a.75.75 0 0 1 0-1.5h6.75V4.5a.75.75 0 0 1 .75-.75Z" clipRule="evenodd" />
                </svg>
              </span>
            </button>

            {isLoading && <Loading text="読み込み中..." />}
            {!isLoading && (
              <div className="coupon-type__table-wrapper">
              <table className="coupon-type__table">
                <thead className="coupon-type__table-head">
                  <tr className="coupon-type__table-row">
                    <th className="coupon-type__table-th coupon-type__table-th--number coupon-type__table-th-rounded-left">番号</th>
                    <th className="coupon-type__table-th">タイプコード</th>
                    <th className="coupon-type__table-th">クーポン名</th>
                    <th className="coupon-type__table-th">金額</th>
                    <th className="coupon-type__table-th">有効期限</th>
                    <th className="coupon-type__table-th">ステータス</th>
                    <th className="coupon-type__table-th">販売可否</th>
                    <th className="coupon-type__table-th coupon-type__table-th-rounded-right">操作</th>
                  </tr>
                </thead>
                <tbody className="coupon-type__table-body">
                  {currentData.map((item, index) => (
                    <tr key={item.id} className="coupon-type__table-row">
                      <td className="coupon-type__table-td coupon-type__table-td--number">{startIndex + index + 1}</td>
                      <td className="coupon-type__table-td coupon-type__table-td--code">{item.typeCode}</td>
                      <td className="coupon-type__table-td">{item.name}</td>
                      <td className="coupon-type__table-td">{formatAmount(item.amount)}</td>
                      <td className="coupon-type__table-td">{formatValidity(item.validityType, item.validityValue)}</td>
                      <td className="coupon-type__table-td">
                        <span className={`coupon-type__status coupon-type__status--${item.status === '有効' ? 'active' : 'inactive'}`}>
                          {item.status}
                        </span>
                      </td>
                      <td className="coupon-type__table-td coupon-type__table-td--center">
                        <label className="coupon-type__checkbox">
                          <input
                            type="checkbox"
                            checked={item.sellable}
                            className="coupon-type__checkbox-input"
                          />
                          <span className="coupon-type__checkbox-custom">
                            <svg viewBox="0 0 24 24" className="coupon-type__checkbox-icon">
                              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" fill="currentColor"/>
                            </svg>
                          </span>
                        </label>
                      </td>
                      <td className="coupon-type__table-td">
                        <div className="coupon-type__actions">
                          <button
                            className="coupon-type__action-btn coupon-type__action-btn--edit"
                            onClick={() => dispatch(openEditModal(item))}
                          >
                            編集
                          </button>
                          <button
                            className="coupon-type__action-btn coupon-type__action-btn--delete"
                            onClick={() => handleDelete(item.id)}
                          >
                            削除
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}

            {!isLoading && couponTypes.length === 0 && (
              <div className="coupon-type__empty">
                <p>データがありません</p>
              </div>
            )}

            {!isLoading && couponTypes.length > 0 && totalPages > 1 && (
              <div className="coupon-type__pagination">
              <button
                className="coupon-type__pagination-btn coupon-type__pagination-btn--nav"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                &lt;
              </button>

              {renderPagination().map((page, index) => (
                <button
                  key={index}
                  className={`coupon-type__pagination-btn ${
                    page === currentPage ? 'coupon-type__pagination-btn--active' : ''
                  } ${page === '...' ? 'coupon-type__pagination-btn--dots' : ''}`}
                  onClick={() => page !== '...' && handlePageChange(page)}
                  disabled={page === '...'}
                >
                  {page}
                </button>
              ))}

              <button
                className="coupon-type__pagination-btn coupon-type__pagination-btn--nav"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                &gt;
              </button>
              </div>
            )}
          </div>
        </div>
{isCreateModalOpen && (
          <div className="coupon-type__modal-overlay">
            <div className="coupon-type__modal">
              <div className="coupon-type__modal-header">
                <h2 className="coupon-type__modal-title">新規クーポン種類作成</h2>
                <button
                  className="coupon-type__modal-close"
                  onClick={() => dispatch(closeCreateModal())}
                >
                  <svg viewBox="0 0 24 24" width="24" height="24">
                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" fill="currentColor"/>
                  </svg>
                </button>
              </div>
              <form onSubmit={handleCreateSubmit} className="coupon-type__modal-form">
                {renderForm(false)}
                <div className="coupon-type__modal-actions">
                  <button
                    type="button"
                    className="coupon-type__modal-btn coupon-type__modal-btn--cancel"
                    onClick={() => dispatch(closeCreateModal())}
                  >
                    キャンセル
                  </button>
                  <button
                    type="submit"
                    className="coupon-type__modal-btn coupon-type__modal-btn--submit"
                    disabled={isLoading}
                  >
                    {isLoading ? '作成中...' : '作成'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
{isEditModalOpen && editingType && (
          <div className="coupon-type__modal-overlay">
            <div className="coupon-type__modal">
              <div className="coupon-type__modal-header">
                <h2 className="coupon-type__modal-title">クーポン種類編集</h2>
                <button
                  className="coupon-type__modal-close"
                  onClick={() => dispatch(closeEditModal())}
                >
                  <svg viewBox="0 0 24 24" width="24" height="24">
                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" fill="currentColor"/>
                  </svg>
                </button>
              </div>
              <form onSubmit={handleEditSubmit} className="coupon-type__modal-form">
                {renderForm(true)}
                <div className="coupon-type__modal-actions">
                  <button
                    type="button"
                    className="coupon-type__modal-btn coupon-type__modal-btn--cancel"
                    onClick={() => dispatch(closeEditModal())}
                  >
                    キャンセル
                  </button>
                  <button
                    type="submit"
                    className="coupon-type__modal-btn coupon-type__modal-btn--submit"
                    disabled={isLoading}
                  >
                    {isLoading ? '保存中...' : '保存'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default CouponType;
